import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { ArrowLeft, ArrowRight, CheckCircle, XCircle, Loader2, Trophy, School } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth, getAuthHeaders } from "../App";
import DashboardHeader from "../components/DashboardHeader";

const TestTake = () => {
  const { testId } = useParams();
  const [searchParams] = useSearchParams();
  const classroomId = searchParams.get('classroom');
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [test, setTest] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [finalResult, setFinalResult] = useState(null);

  useEffect(() => {
    fetchTest();
  }, [testId]);

  const fetchTest = async () => {
    try {
      const response = await axios.get(`${API}/tests/${testId}`, {
        headers: getAuthHeaders(token)
      });
      setTest(response.data);
    } catch (error) {
      toast.error("Failed to load test");
      if (classroomId) {
        navigate(`/classrooms/${classroomId}`);
      } else {
        navigate("/dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAnswer = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleNext = () => {
    if (currentIndex < test.questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSubmitTest = async () => {
    // Check if all questions are answered
    const unanswered = test.questions.filter(q => !answers[q.question_id]);
    if (unanswered.length > 0) {
      toast.error(`Please answer all questions. ${unanswered.length} question(s) remaining.`);
      return;
    }

    setSubmitting(true);
    try {
      // Calculate score
      let correctCount = 0;
      const results = test.questions.map(q => {
        const userAnswer = answers[q.question_id];
        // Case-insensitive comparison for true/false and other answers
        const normalizedUserAnswer = String(userAnswer || '').toLowerCase().trim();
        const normalizedCorrectAnswer = String(q.correct_answer || '').toLowerCase().trim();
        const isCorrect = normalizedUserAnswer === normalizedCorrectAnswer;
        if (isCorrect) correctCount++;
        return {
          question_id: q.question_id,
          user_answer: userAnswer,
          correct_answer: q.correct_answer,
          is_correct: isCorrect
        };
      });

      const score = correctCount;
      const total = test.questions.length;
      const percentage = Math.round((score / total) * 100);

      // If this is a classroom test, submit the score
      if (classroomId) {
        await axios.post(`${API}/classrooms/${classroomId}/submit-score`, {
          test_id: testId,
          score: score,
          total: total
        }, {
          headers: getAuthHeaders(token)
        });
      }

      setFinalResult({
        score,
        total,
        percentage,
        results
      });
      setCompleted(true);
      
      toast.success(`Test completed! Score: ${score}/${total} (${percentage}%)`);
    } catch (error) {
      toast.error("Failed to submit test");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#4F46E5]" />
      </div>
    );
  }

  if (!test) return null;

  const currentQuestion = test.questions?.[currentIndex];
  const progress = test.questions ? ((currentIndex + 1) / test.questions.length) * 100 : 0;
  const answeredCount = Object.keys(answers).length;

  // Safety check for current question
  if (!currentQuestion) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500">Loading question...</p>
        </div>
      </div>
    );
  }

  // Show results screen
  if (completed && finalResult) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <DashboardHeader />
        <main className="container max-w-3xl mx-auto py-8 px-4">
          <Card className="card-base overflow-hidden">
            <div className={`p-8 text-center ${
              finalResult.percentage >= 70 
                ? 'bg-gradient-to-br from-green-500 to-emerald-600' 
                : finalResult.percentage >= 50
                ? 'bg-gradient-to-br from-yellow-500 to-orange-500'
                : 'bg-gradient-to-br from-red-500 to-rose-600'
            }`}>
              <Trophy className="w-16 h-16 text-white mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-white mb-2">Test Completed!</h1>
              <p className="text-white/90 text-lg">{test.title}</p>
            </div>
            
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <div className="text-6xl font-bold text-slate-900 mb-2">
                  {finalResult.percentage}%
                </div>
                <p className="text-xl text-slate-600">
                  {finalResult.score} out of {finalResult.total} correct
                </p>
                <p className="text-slate-500 mt-2">
                  {finalResult.percentage >= 70 ? "Great job! 🎉" : 
                   finalResult.percentage >= 50 ? "Good effort! Keep practicing." : 
                   "Keep studying, you'll do better next time!"}
                </p>
              </div>

              {/* Results breakdown */}
              <div className="space-y-4 mb-8">
                <h3 className="font-semibold text-slate-900">Results Breakdown</h3>
                {finalResult.results.map((result, index) => (
                  <div 
                    key={result.question_id}
                    className={`p-4 rounded-xl border ${
                      result.is_correct 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {result.is_correct ? (
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-slate-900 text-sm">
                          Question {index + 1}: {test.questions[index].question}
                        </p>
                        <p className="text-sm mt-1">
                          <span className="text-slate-500">Your answer:</span>{" "}
                          <span className={result.is_correct ? "text-green-700" : "text-red-700"}>
                            {result.user_answer}
                          </span>
                        </p>
                        {!result.is_correct && (
                          <p className="text-sm mt-1">
                            <span className="text-slate-500">Correct answer:</span>{" "}
                            <span className="text-green-700">{result.correct_answer}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-center gap-4">
                {classroomId ? (
                  <Button 
                    onClick={() => navigate(`/classrooms/${classroomId}`)}
                    className="btn-primary gap-2"
                  >
                    <School className="w-4 h-4" />
                    Back to Classroom
                  </Button>
                ) : (
                  <Button 
                    onClick={() => navigate("/dashboard")}
                    className="btn-primary"
                  >
                    Back to Dashboard
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <DashboardHeader />
      <main className="container max-w-3xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => classroomId ? navigate(`/classrooms/${classroomId}`) : navigate("/dashboard")}
            className="gap-2 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            {classroomId ? "Back to Classroom" : "Back"}
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{test.title}</h1>
              <p className="text-slate-500">
                Question {currentIndex + 1} of {test.questions.length} • {answeredCount} answered
              </p>
            </div>
          </div>
          
          <Progress value={progress} className="mt-4 h-2" />
        </div>

        {/* Question Card */}
        <Card className="card-base mb-6">
          <CardHeader>
            <CardTitle className="text-lg">
              {currentQuestion.question}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {/* Handle True/False questions */}
              {currentQuestion.type === 'true_false' ? (
                <>
                  {['True', 'False'].map((option) => {
                    const optionValue = option.toLowerCase();
                    const isSelected = answers[currentQuestion.question_id] === optionValue;
                    return (
                      <button
                        key={option}
                        onClick={() => handleSelectAnswer(currentQuestion.question_id, optionValue)}
                        className={`w-full p-4 text-left rounded-xl border-2 transition-all ${
                          isSelected
                            ? 'border-[#4F46E5] bg-indigo-50'
                            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                            isSelected 
                              ? 'border-[#4F46E5] bg-[#4F46E5]' 
                              : 'border-slate-300'
                          }`}>
                            {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                          </div>
                          <span className={isSelected ? 'text-[#4F46E5] font-medium' : 'text-slate-700'}>
                            {option}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </>
              ) : (
                /* Handle Multiple Choice questions */
                (currentQuestion.options || []).map((option, idx) => {
                  const isSelected = answers[currentQuestion.question_id] === option;
                  return (
                    <button
                      key={idx}
                      onClick={() => handleSelectAnswer(currentQuestion.question_id, option)}
                      className={`w-full p-4 text-left rounded-xl border-2 transition-all ${
                        isSelected
                          ? 'border-[#4F46E5] bg-indigo-50'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          isSelected 
                            ? 'border-[#4F46E5] bg-[#4F46E5]' 
                            : 'border-slate-300'
                        }`}>
                          {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                        </div>
                        <span className={isSelected ? 'text-[#4F46E5] font-medium' : 'text-slate-700'}>
                          {option}
                        </span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Previous
          </Button>

          <div className="flex gap-2 flex-wrap justify-center">
            {/* Question indicators */}
            {(test.questions || []).map((q, idx) => (
              <button
                key={q.question_id || idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-8 h-8 rounded-full text-sm font-medium transition-all ${
                  idx === currentIndex
                    ? 'bg-[#4F46E5] text-white'
                    : answers[q.question_id]
                    ? 'bg-green-100 text-green-700 border border-green-300'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {idx + 1}
              </button>
            ))}
          </div>

          {currentIndex === test.questions.length - 1 ? (
            <Button
              onClick={handleSubmitTest}
              disabled={submitting}
              className="btn-primary gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Submit Test
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              className="btn-primary gap-2"
            >
              Next
              <ArrowRight className="w-4 h-4" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
};

export default TestTake;
